import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";
import { NorthamptonshireRecordType, NorthamptonshireRecordSubType } from "./NorthantsRecordTypes";
import { IWmsLayerGroupConfig } from "esdm-map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: NorthamptonshireRecordType,
        recordSubType: NorthamptonshireRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case NorthamptonshireRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case NorthamptonshireRecordType.Event:
                layerConfig = this.Event(opts);
                break;   

            //case NorthamptonshireRecordType.AreaBoundary:
            //    layerConfig = this.AreaBoundary();
            //    break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case NorthamptonshireRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case NorthamptonshireRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case NorthamptonshireRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case NorthamptonshireRecordSubType.RegisteredBattlefields:
                    layerConfig = this.RegisteredBattlefields(opts);
                    break; 

                case NorthamptonshireRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break; 

                case NorthamptonshireRecordSubType.LocallyListedBuilding:
                    layerConfig = this.LocallyListedBuilding(opts);
                    break; 
           
            }
        }

        return layerConfig;
    }

    //private AreaBoundary = (): IWmsLayerGroupConfig => {
    //    return {
    //        displayName: "Northamptonshire Boundary",
    //        ref: "AreaBoundary",
    //        type: NorthamptonshireRecordType.AreaBoundary,
    //        url: this.mapFileUrl,
    //        displayInLayerSwitcher: false,
    //        displayInLegend: false,
    //        visibleOnLoad: true,
    //        queryable: false,
    //        layers: [
    //            {
    //                layerName: "AreaBoundary",
    //                minZoom: 1,
    //                maxZoom: 19
    //            }
    //        ]
    //    }
    //}

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: NorthamptonshireRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                //legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: NorthamptonshireRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,    
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                //legend: { line: false, polygon: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private LocallyListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Locally Listed Buildings",
                ref: "LocallyListedBuildings",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.LocallyListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.LocallyListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false, point: false},
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    
    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.ConservationArea },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
    
    private RegisteredBattlefields = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Battlefields",
                ref: "RegisteredBattlefields",
                type: NorthamptonshireRecordType.Designation,
                subType: NorthamptonshireRecordSubType.RegisteredBattlefields,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: NorthamptonshireRecordSubType.RegisteredBattlefields },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }


}

